
import { computed, defineComponent, PropType, reactive, ref } from 'vue'
import { saveSettings } from '../api'
import { HotWindowsSettings } from '../types'
import HwIcon from './HwIcon.vue'

// Функция не считает окна короче часа, поэтому варианты — как в макете.
const GAP_OPTIONS = [
    { value: 60, title: 'От 1 ч' },
    { value: 90, title: 'От 1,5 ч' },
    { value: 120, title: 'От 2 ч' },
    { value: 180, title: 'От 3 ч' },
    { value: 240, title: 'От 4 ч' },
]

const inRange = (value: unknown, min: number, max: number) => {
    const text = String(value ?? '').trim()
    const number = Number(text)
    return text !== '' && Number.isSafeInteger(number) && number >= min && number <= max
}

export default defineComponent({
    name: 'HotWindowsSettingsDrawer',
    components: { HwIcon },
    props: {
        settings: { type: Object as PropType<HotWindowsSettings>, required: true },
        projectId: { type: String, required: true },
    },
    emits: ['close', 'saved', 'toast'],
    setup(props, { emit }) {
        // Черновик отвязан от сохранённого: «Отмена» возвращает то, что
        // реально применено к расчёту.
        const draft = reactive({
            minGapMinutes: GAP_OPTIONS.some(option => option.value === props.settings.minGapMinutes)
                ? props.settings.minGapMinutes
                : 60,
            maxCandidatesPerWindow: Math.min(5, props.settings.maxCandidatesPerWindow) as number | string,
            baseMinDaysSinceService: props.settings.baseMinDaysSinceService as number | string,
            baseMaxCycleDaysAhead: props.settings.baseMaxCycleDaysAhead as number | string,
            baseMaxCycleDaysAfter: props.settings.baseMaxCycleDaysAfter as number | string,
            historyMonths: props.settings.historyMonths as number | string,
            // null — без ограничения, в поле пусто.
            baseMaxDaysSinceService: (props.settings.baseMaxDaysSinceService ?? '') as number | string,
            startedWindowOffsetMinutes: props.settings.startedWindowOffsetMinutes as number | string,
        })
        const saving = ref(false)

        const valid = computed(() => ({
            maxCandidatesPerWindow: inRange(draft.maxCandidatesPerWindow, 1, 5),
            baseMinDaysSinceService: inRange(draft.baseMinDaysSinceService, 0, 365),
            baseMaxCycleDaysAhead: inRange(draft.baseMaxCycleDaysAhead, 0, 100),
            baseMaxCycleDaysAfter: inRange(draft.baseMaxCycleDaysAfter, 0, 100),
            historyMonths: inRange(draft.historyMonths, 1, 60),
            // Функция подняла бы максимум до минимума молча — лучше сказать сразу.
            baseMaxDaysSinceService:
                String(draft.baseMaxDaysSinceService ?? '').trim() === '' ||
                (inRange(draft.baseMaxDaysSinceService, 0, 3650) &&
                    Number(draft.baseMaxDaysSinceService) >= Number(draft.baseMinDaysSinceService)),
            startedWindowOffsetMinutes: inRange(draft.startedWindowOffsetMinutes, 0, 720),
        }))
        const allValid = computed(() => Object.values(valid.value).every(Boolean))

        async function save() {
            if (!allValid.value) return
            saving.value = true
            const { data, error } = await saveSettings({
                projectId: props.projectId,
                settings: {
                    minGapMinutes: Number(draft.minGapMinutes),
                    maxCandidatesPerWindow: Number(draft.maxCandidatesPerWindow),
                    baseMinDaysSinceService: Number(draft.baseMinDaysSinceService),
                    baseMaxCycleDaysAhead: Number(draft.baseMaxCycleDaysAhead),
                    baseMaxCycleDaysAfter: Number(draft.baseMaxCycleDaysAfter),
                    historyMonths: Number(draft.historyMonths),
                    baseMaxDaysSinceService:
                        String(draft.baseMaxDaysSinceService ?? '').trim() === ''
                            ? null
                            : Number(draft.baseMaxDaysSinceService),
                    startedWindowOffsetMinutes: Number(draft.startedWindowOffsetMinutes),
                },
            })
            saving.value = false
            if (error || !data) {
                emit('toast', 'Не удалось сохранить настройки')
                return
            }
            emit('saved', data)
        }

        return { draft, valid, allValid, saving, save, gapOptions: GAP_OPTIONS }
    },
})
